import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { FlowNavigation } from '@/components/FlowNavigation';
import PasswordIcon from '@/assets/icons/password.svg?react';
import './ForgotPassword.scss';

type Step = 'request' | 'verify' | 'reset' | 'done';

const PASSWORD_POLICY_MESSAGE =
    '비밀번호는 8~16자이며 영문, 숫자, 특수문자(!@#$%^&*)를 각각 1자 이상 포함해야 합니다.';
const PASSWORD_POLICY_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,16}$/;

export function ForgotPassword() {
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('request');
    const [studentNumber, setStudentNumber] = useState('');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleRequestCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const response = await fetch('/api/center/auth/password-reset/request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    studentNumber: Number(studentNumber),
                    email
                })
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || '인증코드 발송에 실패했습니다.');
            }

            setStep('verify');
        } catch (err: any) {
            setError(err.message || '인증코드 발송 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const response = await fetch('/api/center/auth/password-reset/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    studentNumber: Number(studentNumber),
                    email,
                    code
                })
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || '인증코드 확인에 실패했습니다.');
            }

            const data = await response.json();
            setResetToken(data.resetToken);
            setStep('reset');
        } catch (err: any) {
            setError(err.message || '인증코드 검증 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!PASSWORD_POLICY_REGEX.test(newPassword)) {
            setError(PASSWORD_POLICY_MESSAGE);
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch('/api/center/auth/password-reset/confirm', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resetToken,
                    newPassword
                })
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || '비밀번호 변경에 실패했습니다.');
            }

            setStep('done');
        } catch (err: any) {
            setError(err.message || '비밀번호 변경 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="forgot-password-page">
            <Header />
            <main className="forgot-password-main">
                <div className="forgot-password-title">
                    <PasswordIcon className="forgot-password-title__icon" />
                    <div className="forgot-password-title__text">
                        <h1>비밀번호 변경</h1>
                        <p>학번과 이메일로 본인 확인 후 비밀번호를 변경하세요</p>
                    </div>
                </div>

                <div className="forgot-password-content">
                    <div className="forgot-password-card">

                        {step === 'request' && (
                            <form onSubmit={handleRequestCode} className="forgot-password-form">
                                <label>학번</label>
                                <input
                                    type="number"
                                    value={studentNumber}
                                    onChange={(e) => setStudentNumber(e.target.value)}
                                    required
                                />

                                <label>이메일</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />

                                <button type="submit" disabled={isLoading}>
                                    {isLoading ? '발송 중...' : '임시 코드 발송'}
                                </button>
                            </form>
                        )}

                        {step === 'verify' && (
                            <form onSubmit={handleVerifyCode} className="forgot-password-form">
                                <p className="desc">이메일로 받은 임시 코드를 입력해주세요.</p>
                                <label>임시 코드</label>
                                <input
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    required
                                />
                                <button type="submit" disabled={isLoading}>
                                    {isLoading ? '확인 중...' : '코드 확인'}
                                </button>
                            </form>
                        )}

                        {step === 'reset' && (
                            <form onSubmit={handleResetPassword} className="forgot-password-form">
                                <p className="desc">{PASSWORD_POLICY_MESSAGE}</p>
                                <label>새 비밀번호</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                />
                                <label>새 비밀번호 확인</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                                <button type="submit" disabled={isLoading}>
                                    {isLoading ? '변경 중...' : '비밀번호 변경'}
                                </button>
                            </form>
                        )}

                        {step === 'done' && (
                            <div className="done">
                                <p>비밀번호가 변경되었습니다.</p>
                                <button onClick={() => navigate('/')}>로그인 화면으로 이동</button>
                            </div>
                        )}

                        {error && <p className="error">{error}</p>}
                    </div>
                </div>
            </main>

            <FlowNavigation
                onPrev={() => navigate(-1)}
                onNext={() => { }}
                hideNextButton
            />
        </div>
    );
}
